import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

/**
 * 🛰️ LIST DECLARATION ATTRIBUTE DEFINITIONS
 * Global, reusable local-capture field templates (e.g. "Electricity
 * consumption") that companies compose into attribute groups.
 * Targets: DeclrAttributeDefinitionController.list (Corp.usp_Z_10_Declr_AttributeDefinition_List)
 */
export const fetchAttributeDefinitions = async (categoryTag, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/list",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ CategoryTag: categoryTag || null })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Declr Attribute Definition List Failed:", err);
        return [];
    }
};

/**
 * 🛰️ ADD DECLARATION ATTRIBUTE DEFINITION
 * Targets: DeclrAttributeDefinitionController.add (Corp.usp_Z_10_Declr_AttributeDefinition_Add)
 */
export const addAttributeDefinition = async (definition, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/add",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(definition)
        );
        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Could not create attribute definition.");
        return res.data;
    } catch (err) {
        console.error("Declr Attribute Definition Add Error:", err);
        throw err;
    }
};

/**
 * 🛰️ UPDATE DECLARATION ATTRIBUTE DEFINITION
 * Targets: DeclrAttributeDefinitionController.update (Corp.usp_Z_10_Declr_AttributeDefinition_Update)
 */
export const updateAttributeDefinition = async (attributeDefinitionId, definition, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/update",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ AttributeDefinitionId: attributeDefinitionId, ...definition })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Could not update attribute definition.");
        return res.data;
    } catch (err) {
        console.error("Declr Attribute Definition Update Error:", err);
        throw err;
    }
};

/**
 * 🛰️ REMOVE (DEACTIVATE) DECLARATION ATTRIBUTE DEFINITION
 * Targets: DeclrAttributeDefinitionController.remove (Corp.usp_Z_10_Declr_AttributeDefinition_Remove)
 */
export const removeAttributeDefinition = async (attributeDefinitionId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/remove",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ AttributeDefinitionId: attributeDefinitionId })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Could not remove attribute definition.");
        return true;
    } catch (err) {
        console.error("Declr Attribute Definition Remove Error:", err);
        throw err;
    }
};

/**
 * 🛰️ ENUM OPTIONS: list / add / remove
 * Only relevant when ValueDataType === 'enum'.
 * Targets: DeclrAttributeDefinitionController.[ListEnumOptions|AddEnumOption|RemoveEnumOption]
 */
export const fetchEnumOptions = async (attributeDefinitionId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/enum-option/list",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ AttributeDefinitionId: attributeDefinitionId })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Enum Option List Failed:", err);
        return [];
    }
};

export const addEnumOption = async (attributeDefinitionId, optionLabel, optionValue, sortOrder, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/enum-option/add",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({
                AttributeDefinitionId: attributeDefinitionId,
                OptionLabel: optionLabel,
                OptionValue: optionValue,
                SortOrder: sortOrder || 0,
            })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Could not add enum option.");
        return res.data;
    } catch (err) {
        console.error("Enum Option Add Error:", err);
        throw err;
    }
};

export const removeEnumOption = async (optionId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/enum-option/remove",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ OptionId: optionId })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Could not remove enum option.");
        return true;
    } catch (err) {
        console.error("Enum Option Remove Error:", err);
        throw err;
    }
};

/**
 * 🛰️ LIST UNITS OF MEASURE
 * Feeds the unit picker; pass unitFamily to constrain (e.g. only ENERGY units).
 * Targets: DeclrAttributeDefinitionController.ListUnits (Ref.usp_GetESG_UnitOfMeasure_List)
 */
export const fetchUnitsOfMeasure = async (unitFamily, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/declr-attribute-definition/unit-of-measure/list",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ UnitFamily: unitFamily || null })
        );
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Unit Of Measure List Failed:", err);
        return [];
    }
};
